import { type DateRange } from "@src/appV2/lib";
import { differenceInMinutes } from "date-fns";

export type TimeAbbreviationOptions = "full" | "abbreviated" | "compact";

const TIME_FORMAT_UNITS = {
  full: {
    hours: { singular: " hour", plural: " hours" },
    minutes: { singular: " minute", plural: " minutes" },
  },
  abbreviated: {
    hours: { singular: " hr", plural: " hrs" },
    minutes: { singular: " min", plural: " min" },
  },
  compact: { hours: { singular: "h", plural: "h" }, minutes: { singular: "m", plural: "m" } },
};

/**
 * Outputs duration between two dates in the format of "X hours YY minutes" or "YY minutes"
 * Examples: "8 hours 30 minutes", "1 hour 30 minutes", "45 minutes", "4 minutes"
 * Handles different abbreviation options: full (X hours YY minutes), abbreviated (X hrs YY min), compact (Xh YYm)
 * Optionally displays hours even if the duration is less than an hour
 * Negative differences are displayed as 0 minutes.
 */
export function formatDateRangeDuration(
  params: DateRange & {
    format?: TimeAbbreviationOptions;
    alwaysDisplayHours?: boolean;
  }
): string {
  const { startDate, endDate, format = "compact", alwaysDisplayHours = true } = params;

  const totalMinutes = Math.max(differenceInMinutes(endDate, startDate), 0);

  const units = TIME_FORMAT_UNITS[format];

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const minuteUnit = minutes === 1 ? units.minutes.singular : units.minutes.plural;

  if (hours !== 0 || alwaysDisplayHours) {
    const paddedMinutes = minutes.toString().padStart(2, "0");
    return `${hours}${
      hours === 1 ? units.hours.singular : units.hours.plural
    } ${paddedMinutes}${minuteUnit}`;
  }

  return `${minutes}${minuteUnit}`;
}
